import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { CURRENT_APP_BUILD_ID, fetchLatestAppBuildId } from '../lib/app-version';

interface AppUpdateBannerProps {
  currentBuildId?: string;
  fetchLatestBuildId?: () => Promise<string | null>;
  reloadPage?: () => void;
  checkIntervalMs?: number;
  /**
   * Anchor edge for the fixed banner. Defaults to 'bottom' to preserve the
   * authenticated app's existing behavior. Public routes pass 'top' to avoid
   * overlapping the sticky accept footer / bottom content.
   */
  position?: 'top' | 'bottom';
}

const DEFAULT_CHECK_INTERVAL_MS = 60_000;

export function AppUpdateBanner({
  currentBuildId = CURRENT_APP_BUILD_ID,
  fetchLatestBuildId = fetchLatestAppBuildId,
  reloadPage = () => window.location.reload(),
  checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS,
  position = 'bottom',
}: AppUpdateBannerProps) {
  const { t } = useTranslation('common');
  const [hasUpdate, setHasUpdate] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    async function checkLatestBuild() {
      try {
        const latestBuildId = await fetchLatestBuildId();
        if (!cancelled && latestBuildId !== null && latestBuildId !== currentBuildId) {
          setHasUpdate(true);
        }
      } catch {
        // Optional update polling must not interrupt the app; MDN documents that
        // fetch rejects only for request-level failures while HTTP status handling
        // belongs to Response.ok checks: https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch
      }
    }

    void checkLatestBuild();

    const intervalId = window.setInterval(() => {
      void checkLatestBuild();
    }, checkIntervalMs);

    window.addEventListener('focus', checkLatestBuild);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkLatestBuild);
    };
  }, [checkIntervalMs, currentBuildId, fetchLatestBuildId]);

  if (!hasUpdate) return null;

  return (
    <div
      role="status"
      data-testid="app-update-banner"
      className={`fixed inset-x-4 ${position === 'top' ? 'top-4' : 'bottom-4'} z-50 mx-auto flex max-w-xl flex-col gap-3 rounded-xl border border-primary/20 bg-background/95 p-4 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between`}
    >
      <div className="flex items-start gap-3 text-sm">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <p className="font-medium text-foreground">{t('appUpdate.title')}</p>
          <p className="text-muted-foreground">{t('appUpdate.message')}</p>
        </div>
      </div>
      <Button type="button" size="sm" onClick={reloadPage} className="shrink-0">
        {t('appUpdate.reload')}
      </Button>
    </div>
  );
}
